import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useCompany, actions as CompanyActions } from 'services/company';
import { useError } from 'contexts/ErrorContext';
import { useAuth } from 'services/auth';
import pricebookAPI from 'services/pricebook/api';
import SearchRules from './content/SearchRules';
import RulesTable from './content/RulesTable';
import './styles.css';

const PricebooksScreen = () => {
    const auth = useAuth();
    const companyContext = useCompany();
    const errorContext = useError();
    const [searchResults, setSearchResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedPricebook, setSelectedPricebook] = useState(null);
    const [companyLoaded, setCompanyLoaded] = useState(false);
    const [lastSearchParams, setLastSearchParams] = useState(null);

    // Load company data on mount if not already loaded - wait for auth
    useEffect(() => {
        if (!auth.state.loggedIn || !auth.state.idToken) {
            console.log('Waiting for auth before loading company...');
            return;
        }

        const hasCompanyData = companyContext.state.company && Object.keys(companyContext.state.company).length > 0;

        if (!companyLoaded && !hasCompanyData) {
            console.log('Loading company data...');
            CompanyActions.refreshCompany(
                companyContext,
                errorContext,
                () => setCompanyLoaded(true),
                (error) => {
                    console.log('Company load error:', error);
                    setCompanyLoaded(true); // Still mark as loaded to prevent infinite retries
                },
            );
        } else if (hasCompanyData) {
            setCompanyLoaded(true);
        }
    }, [companyLoaded, companyContext.state.company, auth.state.loggedIn, auth.state.idToken]);

    // Load company pricebooks on mount
    useEffect(() => {
        if (!auth.state.loggedIn || !auth.state.idToken) return;

        const loadPricebooks = async () => {
            try {
                const response = await pricebookAPI.getPricebooks();
                if (response?.data?.status === 200) {
                    const pricebooks = response.data.payload || [];
                    console.log('Loaded company pricebooks:', pricebooks);
                    // Look for 'Default Price book' to use for adding rules
                    if (pricebooks.length > 0 && !selectedPricebook) {
                        const defaultPricebook = pricebooks.find(pb => pb.name === 'Default Price book');
                        console.log('Default pricebook:', defaultPricebook);
                        const pricebookToUse = defaultPricebook || pricebooks[0];
                        // Ensure company_name is set
                        /* eslint-disable camelcase */
                        if (pricebookToUse && !pricebookToUse.company_name) {
                            pricebookToUse.company_name = company?.name;
                        }
                        /* eslint-enable camelcase */
                        console.log('Using pricebook for rules:', pricebookToUse);
                        setSelectedPricebook(pricebookToUse);
                    }
                }
            } catch (error) {
                console.error('Error loading company pricebooks:', error);
            }
        };

        loadPricebooks();
    }, [auth.state.loggedIn, auth.state.idToken]);

    const company = companyContext.state.company;
    const isRepairer = company?.marketplace_side === 'Repairer';

    // Debug: Check what we're getting
    console.log('Company data:', company);
    console.log('marketplace_side:', company?.marketplace_side);
    console.log('isRepairer:', isRepairer);

    // Show warning if user is not a repairer (but don't block the UI)
    const showRepairerWarning = companyLoaded && company && Object.keys(company).length > 0 && !isRepairer;

    const refreshSearch = async () => {
        if (!lastSearchParams) return;
        setLoading(true);
        try {
            const response = await pricebookAPI.searchPricebooks(lastSearchParams);
            if (response?.data?.status === 200) {
                setSearchResults(response.data.payload || []);
            }
        } catch (e) {
            console.error('Refresh search error:', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h4' sx={{ mb: 3 }}>
                Pricebook Management
            </Typography>

            {showRepairerWarning && (
                <Alert severity='warning' sx={{ mb: 3 }}>
                    This feature is intended for repairer companies.
                </Alert>
            )}

            <SearchRules
                onSearchResults={(results) => {
                    setSearchResults(results);
                    // Don't overwrite selectedPricebook - keep using 'Default Price book' for adding rules
                }}
                onSearchPerformed={(params, results) => {
                    setLastSearchParams(params);
                    setSearchResults(results);
                }}
                setLoading={setLoading}
                errorContext={errorContext}
            />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && searchResults && (
                <>
                    {searchResults.length === 0 && (
                        <Alert severity='info' sx={{ mt: 3, mb: 3 }}>
                            No rules found matching your search criteria.
                        </Alert>
                    )}
                    {selectedPricebook && (
                        <RulesTable
                            pricebook={selectedPricebook}
                            displayPricebook={searchResults.length > 0 ? {
                                ...searchResults[0],
                                /* eslint-disable-next-line camelcase */
                                company_name: company?.name, // Add company name from context
                            } : null}
                            errorContext={errorContext}
                            onRulesUpdated={() => {
                                refreshSearch();
                            }}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default PricebooksScreen;
